/**
 * META2-04 (PR #352): AiConsentModal
 *
 * Modal de consentimiento Ley 172-13 RD Art. 5.4 — explicito, informado, revocable.
 *
 * Se muestra cuando user intenta usar IA por primera vez (o consent revocado /
 * versión obsoleta). Lista qué datos se procesan, qué providers externos
 * reciben los datos, y captura aceptación explícita.
 */
import React, { useState } from 'react';
import { useAiConsent, CURRENT_CONSENT_VERSION } from '../hooks/useAiConsent';
import { LEGAL_LINKS } from '@/shared/constants/legal-links';

export interface AiConsentModalProps {
  open: boolean;
  onAccept?: () => void;
  onReject?: () => void;
}

const DATA_CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'property_data', label: 'Datos de propiedades del catálogo' },
  { key: 'lead_info', label: 'Información de leads (nombre, contacto)' },
  { key: 'conversation_history', label: 'Historial de conversaciones con la IA' },
  { key: 'payment_metadata', label: 'Metadata de pagos (no incluye números de tarjeta)' },
];

const PROVIDERS: Array<{ key: string; label: string }> = [
  { key: 'anthropic', label: 'Anthropic (Claude — Estados Unidos)' },
  { key: 'openai', label: 'OpenAI (GPT — Estados Unidos)' },
  { key: 'google', label: 'Google (Gemini — Estados Unidos)' },
];

export const AiConsentModal: React.FC<AiConsentModalProps> = ({
  open,
  onAccept,
  onReject,
}) => {
  const { grant, isLoading } = useAiConsent();
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    DATA_CATEGORIES.map((c) => c.key),
  );
  const [selectedProviders, setSelectedProviders] = useState<string[]>(
    PROVIDERS.map((p) => p.key),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const toggle = (
    list: string[],
    setter: (next: string[]) => void,
    key: string,
  ) => {
    setter(list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);
  };

  const handleAccept = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await grant({
        consentVersion: CURRENT_CONSENT_VERSION,
        dataCategories: selectedCategories,
        providers: selectedProviders,
      });
      onAccept?.();
    } catch (err: any) {
      setError(err?.message ?? 'No se pudo registrar el consentimiento. Reintentar.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ai-consent-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <h2 id="ai-consent-title" className="mb-3 text-xl font-bold">
          Consentimiento de procesamiento por IA
        </h2>

        <p className="mb-4 text-sm text-gray-600">
          Para activar las funciones de IA, necesitamos su consentimiento explícito
          conforme a la <strong>Ley 172-13 de Protección de Datos Personales</strong>
          {' '}de la República Dominicana, artículo 6.
        </p>

        <section className="mb-4">
          <h3 className="mb-2 font-semibold">¿Qué datos procesamos?</h3>
          <p className="mb-2 text-xs text-gray-500">
            Antes de enviar a los proveedores externos, anonimizamos cédulas, RNC,
            teléfonos, emails y otros identificadores personales.
          </p>
          <div className="space-y-1">
            {DATA_CATEGORIES.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(c.key)}
                  onChange={() => toggle(selectedCategories, setSelectedCategories, c.key)}
                />
                {c.label}
              </label>
            ))}
          </div>
        </section>

        <section className="mb-4">
          <h3 className="mb-2 font-semibold">¿A qué proveedores externos?</h3>
          <p className="mb-2 text-xs text-gray-500">
            Sus datos anonimizados serán transferidos a estos proveedores en
            Estados Unidos. Puede desmarcar los que prefiera no usar.
          </p>
          <div className="space-y-1">
            {PROVIDERS.map((p) => (
              <label key={p.key} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedProviders.includes(p.key)}
                  onChange={() => toggle(selectedProviders, setSelectedProviders, p.key)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </section>

        <section className="mb-4 rounded bg-gray-50 p-3 text-xs text-gray-600">
          <p>
            <strong>Sus derechos (Ley 172-13):</strong> puede revocar este
            consentimiento en cualquier momento desde Configuración → Privacidad.
            La revocación detiene el procesamiento futuro pero no afecta el
            procesamiento ya realizado.
          </p>
          <p className="mt-1">
            Política completa:{' '}
            <a href={LEGAL_LINKS.privacidad} target="_blank" rel="noreferrer" className="underline">
              política de privacidad
              <span className="sr-only"> (abre en una pestaña nueva)</span>
            </a>
          </p>
        </section>

        {error && (
          <div className="mb-3 rounded border border-red-300 bg-red-50 p-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onReject}
            disabled={submitting}
            className="rounded px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            No, ahora no
          </button>
          <button
            type="button"
            onClick={handleAccept}
            disabled={submitting || isLoading || selectedCategories.length === 0}
            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Registrando...' : 'Acepto el procesamiento por IA'}
          </button>
        </div>
      </div>
    </div>
  );
};
